import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MedicationsSection } from "@/features/patient/medications/components/MedicationsView";
import { ReviewItemView } from "@/features/patient/medications/components/ReviewItemView";
import { useMedications } from "@/portals/patient/context/MedicationsContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useProfileFromQuery } from "@/portals/patient/hooks/useProfileFromQuery";
import { listReviewItemsApi, type ReviewItem } from "@/portals/patient/api/reviewItemsApi";
import { useReviewItemsCountStore } from "@/stores/reviewItemsCountStore";
import { AppModal } from "@/shared/components/dialog";
import { PageSpinner } from "@/shared/components/spinner";

/** Resolved review items for the viewed profile (Module 5 "Completed reviews"). */
function useCompletedReviews(refreshKey: unknown): ReviewItem[] {
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const [items, setItems] = useState<ReviewItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    listReviewItemsApi("all", actingAsProfileId).then(({ data }) => {
      if (!cancelled) setItems(data?.items.filter((i) => i.itemStatus === "RESOLVED_CLINICIAN_VERIFIED") ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [actingAsProfileId, refreshKey]);

  return items;
}

export function MedicationsPage() {
  useProfileFromQuery();
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const { medications, isLoading, error, refresh } = useMedications();
  const refreshCount = useReviewItemsCountStore((s) => s.refresh);
  // Refetched whenever the records are, so a just-resolved item shows up with them.
  const completedReviews = useCompletedReviews(medications);
  // The review outcome opens over the Medications tab (no page change). A
  // "review complete" notification links here with ?review=<id> to open it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openReviewId, setOpenReviewId] = useState<string | null>(() => searchParams.get("review"));
  const navigate = useNavigate();
  useEffect(() => {
    const requested = searchParams.get("review");
    if (!requested) return;
    setOpenReviewId(requested);
    setSearchParams((params) => {
      params.delete("review");
      return params;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Spinner only before anything has loaded — a later refresh (e.g. right
  // after answering a review) must never unmount the page and its open modal.
  if (isLoading && medications.length === 0) {
    return <PageSpinner className="bg-[#F7F7F7]" />;
  }

  if (error) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-[#F7F7F7] py-24 text-center">
        <p className="text-sm font-semibold text-[#F36F8F]">{error}</p>
      </div>
    );
  }

  return (
    <>
      <MedicationsSection
        medications={medications}
        completedReviews={completedReviews}
        onReviewItem={setOpenReviewId}
        onOpenReviewQueue={() => navigate("/patient/human-review-queue")}
      />
      <AppModal
        open={openReviewId !== null}
        onOpenChange={(open) => { if (!open) setOpenReviewId(null); }}
        title={<span className="text-[#0A2947]">Medication review</span>}
        description={<span className="text-[#4B5563]">Tell us about this medicine so your clinician can confirm it.</span>}
        width={860}
        footer={null}
      >
        {openReviewId && (
          <ReviewItemView
            // Remount per item/profile so a previous item's answer never carries over.
            key={`${actingAsProfileId ?? "self"}:${openReviewId}`}
            variant="modal"
            itemId={openReviewId}
            actingAsProfileId={actingAsProfileId}
            onBack={() => setOpenReviewId(null)}
            onItemChanged={() => {
              refresh();
              void refreshCount(actingAsProfileId);
            }}
          />
        )}
      </AppModal>
    </>
  );
}
